/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import { PagerSorter } from "@Internal/Models";
import { Filter } from "@Internal/Contracts";
import { CustomError } from "@Internal/Models";

import { ConsoleUsers, Roles } from "App/IOC/Services";
import { Role, User } from "App/Models";
import { $t } from "App/IOC";

import Columns from "./Columns";
import Filters from "./Filters";

function StateLabel( state?: string ): string {
    switch( state ) {
        case "active": return $t( "CONSOLE_USERS_stateActive" );
        case "pending": return $t( "CONSOLE_USERS_statePending" );
        case "disabled": return $t( "CONSOLE_USERS_stateDisabled" );
        default: return $t( "GLOBAL_unknown" );
    }
}

/* Decorate users with the flat display fields the table renders (it reads record[ column.Id ]).
   The role name comes from the hydrated Role when present, otherwise from the loaded roles map. */
function Decorate( users: User[], roles: Role[] ): void {
    const byId: Map<number, Role> = new Map();

    for( const role of roles )
        byId.set( role.Id, role );

    for( const user of users ) {
        const anyUser = user as any;

        anyUser.DisplayName = user.Name !== "" ? user.Name : ( user.Email || "" );
        anyUser.StateLabel = StateLabel( user.State );

        const role = user.Role !== undefined
            ? user.Role
            : ( user.RoleId !== undefined ? byId.get( user.RoleId ) : undefined );

        anyUser.RoleName = role !== undefined && role.Name !== undefined
            ? role.Name
            : $t( "CONSOLE_USERS_noRole" );
    }
}

/* Load every role once (for the table name map and the assign-role select). */
async function GetRoles(): Promise<[ Role[], CustomError | undefined ]> {
    const [ roles, , error ] = await Roles.Find( [], new PagerSorter( 100 ) );

    return [ roles, error ];
}

async function Find(
    filters: Filter[],
    pagerSorter: PagerSorter
): Promise<[ User[], PagerSorter, CustomError | undefined ]> {
    const [ users, updatedPagerSorter, error ] = await ConsoleUsers.Find( filters, pagerSorter );

    if( error !== undefined )
        return [ [], updatedPagerSorter, error ];

    const [ roles ] = await GetRoles();

    Decorate( users, roles );

    return [ users, updatedPagerSorter, undefined ];
}

async function Get( id: number ): Promise<[ User | undefined, CustomError | undefined ]> {
    const [ user, error ] = await ConsoleUsers.Get( id );

    if( error !== undefined || user === undefined )
        return [ undefined, error ];

    const [ roles ] = await GetRoles();

    Decorate( [ user ], roles );

    return [ user, undefined ];
}

export default {
    Columns,
    Filters,
    GetRoles,
    StateLabel,
    Find,
    Get,
    Update: ConsoleUsers.Update,
    Invite: ConsoleUsers.Invite,
    Disable: ConsoleUsers.Disable,
    Reactivate: ConsoleUsers.Reactivate,
    Reinvite: ConsoleUsers.Reinvite,
    AssignRole: ConsoleUsers.AssignRole
};
